/**
 * MediaThumbnail - Componente de thumbnail inteligente por tipo de arquivo
 * Exibe preview visual para imagens/vídeos e ícones apropriados para outros tipos
 */

import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';
import { 
  FileText, 
  File, 
  Music, 
  Video, 
  Play,
  Image as ImageIcon 
} from 'lucide-react';

interface MediaThumbnailProps {
  /** Tipo do anexo: image, video, audio, document, sticker */
  type: string;
  /** URL do arquivo (pode ser relativa do storage) */
  url: string;
  /** URL da thumbnail (se disponível) */
  thumbnailUrl?: string | null;
  /** Nome do arquivo para fallback */
  filename?: string | null;
  /** Mime type para identificação mais precisa */
  mimeType?: string | null;
  /** Tamanho do thumbnail em pixels */
  size?: number;
  /** Classes CSS adicionais */
  className?: string;
  /** Handler de clique */
  onClick?: () => void;
}

export function MediaThumbnail({
  type,
  url,
  thumbnailUrl,
  filename,
  mimeType,
  size = 64,
  className,
  onClick,
}: MediaThumbnailProps) {
  // Converte URLs relativas para absolutas
  const fullUrl = getStorageUrl(url);
  const fullThumbnailUrl = thumbnailUrl ? getStorageUrl(thumbnailUrl) : null;

  // Determina se é PDF baseado no mime type ou extensão
  const isPdf = mimeType?.includes('pdf') || 
    filename?.toLowerCase().endsWith('.pdf') ||
    url?.toLowerCase().includes('.pdf');

  // Determina se é documento Excel/Word
  const isSpreadsheet = mimeType?.includes('spreadsheet') || 
    mimeType?.includes('excel') ||
    filename?.toLowerCase().match(/\.(xlsx?|csv)$/);
  
  const isWord = mimeType?.includes('word') ||
    filename?.toLowerCase().match(/\.(docx?)$/);

  // Renderiza thumbnail de imagem (inclui sticker e gif)
  if (type === 'image' || type === 'sticker') {
    return (
      <div
        className={cn(
          'rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-80 transition-opacity',
          className
        )}
        style={{ width: size, height: size }}
        onClick={onClick}
      >
        <img
          src={fullThumbnailUrl || fullUrl}
          alt={filename || 'Imagem'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  // Renderiza thumbnail de vídeo (com preview real usando primeiro frame)
  if (type === 'video') {
    return (
      <div
        className={cn(
          'rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-80 transition-opacity relative',
          className
        )}
        style={{ width: size, height: size }}
        onClick={onClick}
      >
        {/* Video element para capturar primeiro frame como preview */}
        <video
          src={fullUrl}
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
          poster={fullThumbnailUrl || undefined}
        />
        {/* Overlay com ícone de play */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <Play className="h-6 w-6 text-white fill-white" />
        </div>
      </div>
    );
  }

  // Renderiza ícone de áudio
  if (type === 'audio') {
    return (
      <div
        className={cn(
          'rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center bg-green-100 dark:bg-green-900/30',
          className
        )}
        style={{ width: size, height: size }}
        onClick={onClick}
      >
        <Music className="h-6 w-6 text-green-600 dark:text-green-400" />
      </div>
    );
  }

  // Renderiza ícone de documento
  if (type === 'document') {
    // PDF - vermelho
    if (isPdf) {
      return (
        <div
          className={cn(
            'rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center bg-red-100 dark:bg-red-900/30',
            className
          )}
          style={{ width: size, height: size }}
          onClick={onClick}
        >
          <FileText className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>
      );
    }

    // Excel - verde
    if (isSpreadsheet) {
      return (
        <div
          className={cn(
            'rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/30',
            className
          )}
          style={{ width: size, height: size }}
          onClick={onClick}
        >
          <FileText className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
        </div>
      );
    }

    // Word - azul
    if (isWord) {
      return (
        <div
          className={cn(
            'rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center bg-blue-100 dark:bg-blue-900/30',
            className
          )}
          style={{ width: size, height: size }}
          onClick={onClick}
        >
          <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
      );
    }

    // Outros documentos - laranja
    return (
      <div
        className={cn(
          'rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center bg-orange-100 dark:bg-orange-900/30',
          className
        )}
        style={{ width: size, height: size }}
        onClick={onClick}
      >
        <File className="h-6 w-6 text-orange-600 dark:text-orange-400" />
      </div>
    );
  }

  // Fallback genérico
  return (
    <div
      className={cn(
        'rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center bg-muted',
        className
      )}
      style={{ width: size, height: size }}
      onClick={onClick}
    >
      <ImageIcon className="h-6 w-6 text-muted-foreground" />
    </div>
  );
}
